import { Button, Modal, Select, useToast } from '@/components';
import { useUsers } from '@/features/users/hooks';
import { type AdminAppleUdid, ApiClientError } from '@uchat-team-management/shared';
import { Show, createSignal } from 'solid-js';
import { useReassignAppleUdid } from './hooks';

interface Props {
  open: boolean;
  onClose: () => void;
  row: AdminAppleUdid;
}

// Move a UDID row to a different user (UCH-448) — same user picker as
// AssignDeviceModal. Registration/verification badges are unaffected.
export const ChangeOwnerModal = (props: Props) => {
  const users = useUsers(() => ({}));
  const reassign = useReassignAppleUdid();
  const toast = useToast();
  const [userId, setUserId] = createSignal(props.row.user.id);
  const [error, setError] = createSignal<string | null>(null);

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    setError(null);
    try {
      await reassign.mutateAsync({ udidId: props.row.id, userId: userId() });
      toast.success('Device moved');
      props.onClose();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.error.message);
      else setError((err as Error).message ?? 'Reassign failed');
    }
  };

  return (
    <Modal open={props.open} onClose={props.onClose} title={`Change user — ${props.row.label}`}>
      <form onSubmit={onSubmit} class="flex flex-col gap-4">
        <Select
          label="User"
          value={userId()}
          onChange={setUserId}
          placeholder="Select a user"
          required
          options={(users.data?.data ?? []).map((u) => ({
            value: u.id,
            label: `${u.displayName || u.email}${u.status === 'pending' ? ' (pending)' : ''}`,
          }))}
        />
        <Show when={error()}>
          <p class="text-red-600 text-xs">{error()}</p>
        </Show>
        <div class="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={userId() === props.row.user.id}
            loading={reassign.isPending}
          >
            Move
          </Button>
        </div>
      </form>
    </Modal>
  );
};
